import { utf8ToBytes } from './encoding';

export const AES_GCM_IV_BYTES = 12;

function requireCrypto() {
  if (!globalThis.crypto || !globalThis.crypto.subtle) {
    throw new Error('WebCrypto is not available in this environment.');
  }
  return globalThis.crypto;
}

export function randomBytes(length: number): Uint8Array {
  const crypto = requireCrypto();
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

export async function importAesKey(keyBytes: Uint8Array): Promise<CryptoKey> {
  const crypto = requireCrypto();
  return crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, [
    'encrypt',
    'decrypt',
  ]);
}

export async function derivePbkdf2KeyBytes(
  passphrase: string,
  salt: Uint8Array,
  iterations: number
): Promise<Uint8Array> {
  const crypto = requireCrypto();
  const baseKey = await crypto.subtle.importKey(
    'raw',
    utf8ToBytes(passphrase),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const derived = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations,
      hash: 'SHA-256',
    },
    baseKey,
    256
  );
  return new Uint8Array(derived);
}

export async function encryptAesGcm(
  keyBytes: Uint8Array,
  iv: Uint8Array,
  plaintext: Uint8Array
): Promise<Uint8Array> {
  const crypto = requireCrypto();
  const key = await importAesKey(keyBytes);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  return new Uint8Array(ciphertext);
}

export async function decryptAesGcm(
  keyBytes: Uint8Array,
  iv: Uint8Array,
  ciphertext: Uint8Array
): Promise<Uint8Array> {
  const crypto = requireCrypto();
  const key = await importAesKey(keyBytes);
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  return new Uint8Array(plaintext);
}

export function xorBytes(left: Uint8Array, right: Uint8Array): Uint8Array {
  if (left.length !== right.length) {
    throw new Error('xorBytes requires equal-length inputs.');
  }
  const output = new Uint8Array(left.length);
  for (let i = 0; i < left.length; i += 1) {
    output[i] = left[i] ^ right[i];
  }
  return output;
}
